import React, { useEffect, useRef } from 'react';
import { PlusCircle } from 'react-bootstrap-icons';

import { MedicationProfileSteps, type MedProfileForm } from './types';
import MedicationProfileTitle from './components/MedicationProfileTitle';
import FinaliseButton from './components/MedicationDetail/FinaliseButton';
import AddMedicationForm from './components/MedicationDetail/addMedication/AddMedicationForm';
import Appointments from './components/MedicationDetail/appointment/Appointments';
import ExportSettings from './components/MedicationDetail/settings/ExportSettings';
import MedicationInfos from './components/MedicationDetail/medicationInfos/MedicationInfos';
import AddOrderSet from './components/MedicationDetail/addOrderSet/AddOrderSet';
import { useSelector } from 'react-redux';
import { SystemPermission, usePatientInsightSettings } from '../../../context';
import {
  Appointment,
  MedProfile,
  MedProfileStatusEnum,
  ViewMode,
} from './hook/types';
import { RootState } from '../../../store';
import { useFieldArray, useForm } from 'react-hook-form';
import {
  useCreateMedProfiles,
  useGetAllCatalogOrderSets,
  useMedicalProfilePdf,
  useUpdateMedProfiles,
} from './hook';
import { isEmpty, omit } from 'lodash';
import CareMapprButton from '../../components/Button/CareMapprButton';
import { defaultMedSectionBreak } from './types';
import { PatientAppInviteCard } from '../../components/WellbeingPlan/PatientAppInviteCard';
import { Col, Row } from 'react-bootstrap';
import actionCompletedIcon from '../../../assets/img/button-icons/action-completed.svg';
import { useInviteMedicationProfilePatient } from './hook/useInviteMedicationProfilePatient';
import { useLocation } from 'react-router-dom';
import { usePatientProfile } from '../../components/ProfileBar/hooks';
import PharmacistDoctorLetterSection from './components/Letters/PharmacistDoctorLetterSection';
import type {
  DoctorLetterForm,
  PharmacistLetterForm,
} from './components/Letters/types';
import { useMedicationProfileDirty } from './context/MedicationProfileDirtyProvider';
import CaremapprConfirmation from '../../components/Confirmation/CaremapprConfirmation';
import { useInitialMeds } from './hook/useInitialMeds';
import { useMedicationProfilesShared } from '../../components/ProfileBar/context/MedicationProfilesProvider';
import CreateMedProfileCatalogueSection from './components/CreateMedProfileCatalogueSection/CreateMedProfileCatalogueSection';
import { useToast } from '../../../context';
import { MedicationProfileCatalogueProvider } from './context/MedicationProfileCatalogueProvider';

interface MedicationProfileDetailProps {
  onChangeTab: (tab: string) => void;
  activeItem: Partial<MedProfile> | null;
  createMedProfile?: boolean;
  shouldPrefillFromTimelineMeds?: boolean;
}

const ensureMedSectionsOrder = (sections: any) => {
  const list = Array.isArray(sections) ? sections : [];
  return list.map((section, index) => ({
    ...section,
    medications: ensureMedicationsOrder(
      sortMedicationsByOrderIfPresent(section?.medications),
    ),
    order: index + 1,
  }));
};

const ensureMedicationsOrder = (medications: any) => {
  const list = Array.isArray(medications) ? medications : [];
  return list.map((medication, index) => ({
    ...medication,
    order: index + 1,
  }));
};

const sortMedicationsByOrderIfPresent = (medications: any) => {
  const list = Array.isArray(medications) ? medications : [];
  const hasCompleteOrder =
    list.length > 0 &&
    list.every((medication) => Number.isFinite(Number(medication?.order)));
  if (!hasCompleteOrder) return list;
  return [...list].sort(
    (a, b) => (Number(a?.order) || 0) - (Number(b?.order) || 0),
  );
};

const sortMedSectionsByOrderIfPresent = (sections: any) => {
  const list = Array.isArray(sections) ? sections : [];
  const hasCompleteOrder =
    list.length > 0 && list.every((s) => Number.isFinite(Number(s?.order)));
  if (!hasCompleteOrder) return list;
  return [...list].sort(
    (a, b) => (Number(a?.order) || 0) - (Number(b?.order) || 0),
  );
};

const hasSequentialMedSectionsOrder = (sections: any) => {
  const list = Array.isArray(sections) ? sections : [];
  return list.every(
    (section, index) =>
      Number(section?.order) === index + 1 &&
      (Array.isArray(section?.medications) ? section.medications : []).every(
        (medication: any, medicationIndex: number) =>
          Number(medication?.order) === medicationIndex + 1,
      ),
  );
};

const MedicationProfileDetail = ({
  onChangeTab,
  activeItem,
  createMedProfile,
  shouldPrefillFromTimelineMeds = false,
}: MedicationProfileDetailProps) => {
  const [openAddMedication, setOpenAddMedication] = React.useState(false);
  const { isDirty } = useMedicationProfileDirty();
  const [confirmResetOpen, setConfirmResetOpen] = React.useState(false);
  const [selectingCatalogue, setSelectingCatalogue] = React.useState(
    Boolean(createMedProfile && shouldPrefillFromTimelineMeds),
  );
  const [isSubmitting, setIsSubmitting] = React.useState<
    'download' | 'preview' | null
  >(null);
  const [openAddOrderSet, setOpenAddOrderSet] = React.useState(false);
  const [showFinaliseSection, setShowFinaliseSection] = React.useState(
    activeItem?.profileStatus === MedProfileStatusEnum.FINALISED,
  );
  const { initialMeds } = useInitialMeds();
  const currentId = useRef(activeItem?.id);
  const { selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );
  const { patientMedProfileActive } = usePatientProfile();
  const { refresh } = useMedicationProfilesShared();
  const { refreshTimeline } = usePatientInsightSettings();
  const { setToast } = useToast();

  const { postGetAllCatalogOrderSets, orderSetData } =
    useGetAllCatalogOrderSets();
  const { postCreatePatientMedProfiles, isLoading: isCreating } =
    useCreateMedProfiles();
  const { postUpdatePatientMedProfiles, isLoading: isUpdating } =
    useUpdateMedProfiles();

  const { inviteMedProfilePatient, isInvitingPatient, sent } =
    useInviteMedicationProfilePatient();

  const initialSections =
    ensureMedSectionsOrder(
      sortMedSectionsByOrderIfPresent(activeItem?.medSections || []).map(
        (section: any) => ({
          ...section,
          medications:
            shouldPrefillFromTimelineMeds && isEmpty(section.medications)
              ? [...initialMeds]
              : section.medications,
        }),
      ),
    ) || [];
  const medicationProfileForm = useForm<MedProfileForm>({
    defaultValues: {
      appointments: activeItem?.appointments || [],
      medSections: initialSections,
      pdfOptions: {
        isViewTime: activeItem?.pdfOptions?.isViewTime ?? true,
        isViewIcon: activeItem?.pdfOptions?.isViewIcon ?? false,
        isDrLetterIncluded: activeItem?.pdfOptions?.isDrLetterIncluded ?? false,
        isPharmLetterIncluded:
          activeItem?.pdfOptions?.isPharmLetterIncluded ?? false,
        isLargeFontSize: activeItem?.pdfOptions?.isLargeFontSize ?? false,
        isSubheadingIncluded:
          activeItem?.pdfOptions?.isSubheadingIncluded ?? false,
        extraRows: activeItem?.pdfOptions?.extraRows ?? 0,
      },
    },
  });

  const { isLoading: isLoadingPdf, postGetPatientMedProfilePdf } =
    useMedicalProfilePdf();

  const { watch, getValues, control, setValue } = medicationProfileForm;

  useEffect(() => {
    if (!shouldPrefillFromTimelineMeds) return;
    if (!initialMeds || initialMeds.length === 0) return;

    const currentSections = getValues('medSections') || [];
    if (currentSections.length === 0) {
      setValue(
        'medSections',
        ensureMedSectionsOrder([
          { medSectionBreak: defaultMedSectionBreak, medications: initialMeds },
        ]),
      );
    } else {
      const updated = ensureMedSectionsOrder(
        currentSections.map((section: any) => ({
          ...section,
          medications:
            Array.isArray(section.medications) && section.medications.length > 0
              ? section.medications
              : initialMeds,
        })),
      );
      setValue('medSections', updated);
    }
  }, [getValues, initialMeds, setValue, shouldPrefillFromTimelineMeds]);

  const {
    append: appendAppointment,
    remove: removeAppointment,
    update: updateAppointment,
  } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormProvider)
    name: 'appointments', // unique name for your Field Array
  });

  const medSections = watch('medSections');
  const appointments = watch('appointments');

  useEffect(() => {
    const currentSections = getValues('medSections') || [];
    if (
      !Array.isArray(currentSections) ||
      currentSections.length === 0 ||
      hasSequentialMedSectionsOrder(currentSections)
    ) {
      return;
    }

    setValue('medSections', ensureMedSectionsOrder(currentSections), {
      shouldDirty: false,
    });
  }, [getValues, medSections, setValue]);

  const setMedSectionsForCatalogue = React.useCallback(
    (nextSections: any) =>
      setValue('medSections', ensureMedSectionsOrder(nextSections)),
    [setValue],
  );

  // Register a getter from Letters section to pull doctor/pharmacist letter values on demand
  const lettersValuesGetterRef = React.useRef<
    | (() => {
        doctorLetter: DoctorLetterForm;
        pharmacistLetter: PharmacistLetterForm;
      })
    | null
  >(null);
  const registerLettersGetter = (
    getter: () => {
      doctorLetter: DoctorLetterForm;
      pharmacistLetter: PharmacistLetterForm;
    },
  ) => {
    lettersValuesGetterRef.current = getter;
  };

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const access_token = searchParams.get('access_token');

  const onAddSection = (
    name: string,
    options?: { position?: 'top'; sectionText?: string; medName?: string },
  ) => {
    const currentSections = getValues('medSections') || [];
    const nextSection: any = {
      medSectionBreak: name,
      medications: [],
    };
    if (options?.sectionText) {
      nextSection.medications = [
        {
          medId: Date.now(),
          medName: options?.medName || name,
          customMed: true,
          furtherInformation: options.sectionText,
        },
      ];
    }

    const nextSections =
      options?.position === 'top'
        ? [
            nextSection,
            ...(Array.isArray(currentSections) ? currentSections : []),
          ]
        : [
            ...(Array.isArray(currentSections) ? currentSections : []),
            nextSection,
          ];
    setValue('medSections', ensureMedSectionsOrder(nextSections));
  };

  const onAddMedicationToSection = (data: any, targetSection: string) => {
    const allSections = getValues('medSections');
    const sections = Array.isArray(allSections) ? allSections : [];
    const importantSection = 'Important patient information';
    const firstNonImportant = sections.find(
      (section) => section.medSectionBreak !== importantSection,
    )?.medSectionBreak;

    const resolvedTargetSection =
      typeof targetSection === 'string' &&
      targetSection.trim().length > 0 &&
      targetSection !== importantSection
        ? targetSection
        : firstNonImportant || defaultMedSectionBreak;

    const trimmedData = omit(data, ['medSectionBreak']);

    const updateSections = sections.map((section) => {
      if (section.medSectionBreak === resolvedTargetSection) {
        return {
          ...section,
          medications: [...section.medications, trimmedData],
        };
      }
      return section;
    });

    const hasTargetSection = sections.some(
      (section) => section.medSectionBreak === resolvedTargetSection,
    );
    if (!hasTargetSection) {
      const nextSections = [
        ...sections,
        {
          medSectionBreak: resolvedTargetSection,
          order: sections.length + 1,
          medications: [trimmedData],
        } as any,
      ];
      setValue('medSections', ensureMedSectionsOrder(nextSections));
      return;
    }

    setValue('medSections', ensureMedSectionsOrder(updateSections));
  };

  const onEditMedicationToSection = (data: any, targetSection: string) => {
    const allSections = getValues('medSections');
    const updateSections = allSections.map((section) => {
      if (section.medSectionBreak === targetSection) {
        return {
          ...section,
          medications: section.medications.map((medication) => {
            if (medication.medId === data.medId) {
              return data;
            }
            return medication;
          }),
        };
      }
      return section;
    });
    setValue('medSections', ensureMedSectionsOrder(updateSections));
  };

  const onEditSection = (name: string, indexToUpdate: number) => {
    const currentSections = getValues('medSections') || [];
    const list = Array.isArray(currentSections) ? currentSections : [];
    const nextSections = list.map((section: any, idx: number) => {
      if (idx !== indexToUpdate) return section;
      return {
        ...section,
        medSectionBreak: name,
      };
    });
    setValue('medSections', ensureMedSectionsOrder(nextSections));
  };

  const onRemoveSection = (indexToRemove: number) => {
    const currentSections = getValues('medSections') || [];
    const nextSections = (
      Array.isArray(currentSections) ? currentSections : []
    ).filter((_: any, idx: number) => idx !== indexToRemove);
    setValue('medSections', ensureMedSectionsOrder(nextSections));
  };

  const onOpenAddMedication = React.useCallback(() => {
    setOpenAddOrderSet(false);
    setOpenAddMedication(true);
  }, []);

  const onAddOrderSet = React.useCallback(() => {
    setOpenAddOrderSet(true);
    setOpenAddMedication(false);
  }, []);

  const onCloseAddMedication = () => {
    setOpenAddMedication(false);
  };

  const onCloseAddOrderSet = () => {
    setOpenAddOrderSet(false);
  };

  const onAddOrderSetToSection = (values: { selectedOrderSetId: number }) => {
    const { selectedOrderSetId } = values;
    const orderSetToAdd = orderSetData.find(
      (item) => item.id === selectedOrderSetId,
    );
    if (orderSetToAdd) {
      const medications = orderSetToAdd.medications
        .filter((i) => !!i.details)
        .map((item) => {
          return { ...item.details, medId: item.medId };
        });
      const allSections = getValues('medSections');
      const nextSections = ensureMedSectionsOrder(
        Array.isArray(allSections) ? allSections : [],
      ).map((s: any) => ({
        ...s,
        medications: Array.isArray(s.medications) ? [...s.medications] : [],
      }));

      for (const currentMedication of medications) {
        const selectedSection = currentMedication?.medSectionBreak
          ? currentMedication.medSectionBreak
          : defaultMedSectionBreak;
        const currentMedicationTrimmed = omit(currentMedication, [
          'medSectionBreak',
        ]);

        const idx = nextSections.findIndex(
          (section: any) => section.medSectionBreak === selectedSection,
        );
        if (idx !== -1) {
          nextSections[idx] = {
            ...nextSections[idx],
            medications: [
              ...nextSections[idx].medications,
              currentMedicationTrimmed,
            ],
          };
          continue;
        }

        nextSections.push({
          medSectionBreak: selectedSection,
          medications: [currentMedicationTrimmed],
        });
      }

      setValue('medSections', ensureMedSectionsOrder(nextSections));
    }
    onCloseAddOrderSet();
  };

  const onRemoveMedicationFromSection = (
    medId: number,
    sectionName: string,
  ) => {
    const allSections = getValues('medSections');
    const updateSections = allSections.map((section) => {
      if (section.medSectionBreak === sectionName) {
        return {
          ...section,
          medications: Array.isArray(section.medications)
            ? section.medications.filter((item) => item.medId !== medId)
            : [],
        };
      }
      return section;
    });
    setValue('medSections', ensureMedSectionsOrder(updateSections));
  };

  const onAddAppointment = (values: Appointment) => {
    appendAppointment(values);
  };

  const onEditAppointment = (values: Appointment, index: number) => {
    updateAppointment(index, values);
  };

  const onAddingIdToMedProfile = (id: string) => {
    currentId.current = id;
  };

  const onFinaliseProfile = async (
    status: MedProfileStatusEnum,
    refreshRequired = true,
    type?: 'preview' | 'download',
  ) => {
    setIsSubmitting(type || null);

    // Show loading toast
    const loadingMessage =
      status === MedProfileStatusEnum.FINALISED
        ? 'Finalising medication profile...'
        : 'Saving medication profile...';
    setToast(loadingMessage, { autohide: false });

    let result = null;
    const lettersValues = lettersValuesGetterRef.current
      ? lettersValuesGetterRef.current()
      : undefined;

    try {
      if (currentId.current) {
        const updatedMedSection = {
          ...activeItem,
          ...getValues(),
          ...(lettersValues
            ? {
                doctorLetter: lettersValues.doctorLetter,
                pharmacistLetter: lettersValues.pharmacistLetter,
              }
            : {}),
          id: currentId.current,
          profileStatus: status,
        } as MedProfile;
        result = await postUpdatePatientMedProfiles(updatedMedSection);
      } else {
        const createdMedSection = {
          ...activeItem,
          ...getValues(),
          ...(lettersValues
            ? {
                doctorLetter: lettersValues.doctorLetter,
                pharmacistLetter: lettersValues.pharmacistLetter,
              }
            : {}),
          profileStatus: status,
        } as MedProfile;
        result = await postCreatePatientMedProfiles(createdMedSection);
        if (result?.id && !currentId.current) {
          onAddingIdToMedProfile(result?.id);
        }
      }

      // Check if save was successful
      if (!result) {
        setToast('Failed to save medication profile. Please try again.', {
          autohide: true,
        });
        setIsSubmitting(null);
        return null;
      }

      // Show success toast
      const successMessage =
        status === MedProfileStatusEnum.FINALISED
          ? 'Medication profile finalised successfully'
          : 'Medication profile saved successfully';
      setToast(successMessage, { autohide: true });

      if (refreshRequired) {
        await refresh();

        if (status === MedProfileStatusEnum.FINALISED) {
          setShowFinaliseSection(true);
          // Refetch timeline after finalizing med profile
          if (access_token && selectedTenancy?.tenancy_id) {
            await refreshTimeline(access_token, selectedTenancy.tenancy_id);
          }
        } else {
          onChangeTab && onChangeTab(MedicationProfileSteps.FULL_LIST);
        }
      }
      setIsSubmitting(null);

      return result;
    } catch (error) {
      console.error('Error in onFinaliseProfile:', error);
      setToast('An error occurred while saving. Please try again.', {
        autohide: true,
      });
      setIsSubmitting(null);
      return null;
    }
  };

  const notableToEdit =
    activeItem?.profileStatus === MedProfileStatusEnum.CONSUMER_CREATED ||
    activeItem?.profileStatus === MedProfileStatusEnum.ENTERED_IN_ERROR ||
    activeItem?.profileStatus === MedProfileStatusEnum.FINALISED ||
    showFinaliseSection;

  const canEditMedicationProfile =
    (!selectedTenancy?.permissionsSetup ||
      (selectedTenancy?.permissionsSetup &&
        selectedTenancy?.userPermissions &&
        !!(selectedTenancy?.userPermissions)[
          SystemPermission.EDIT_MEDICATION_PROFILE
        ])) &&
    !notableToEdit;

  const onPreview = async () => {
    let activeItemId = currentId.current;
    if (
      !activeItemId ||
      activeItem?.profileStatus === MedProfileStatusEnum.IN_PROGRESS
    ) {
      const result = await onFinaliseProfile(
        MedProfileStatusEnum.IN_PROGRESS,
        false,
        'preview',
      );
      if (result) {
        activeItemId = currentId.current;
      }
    }
    if (activeItemId && access_token) {
      const values = medicationProfileForm.getValues();
      postGetPatientMedProfilePdf('preview', {
        payload: {
          medProfileId: activeItemId,
          access_token: access_token,
        },
        pdfOptions: {
          viewMode: ViewMode.PREVIEW,
        },
        options: {
          isViewTime: values.pdfOptions?.isViewTime ?? true,
          isViewIcon: values.pdfOptions?.isViewIcon ?? false,
          isDrLetterIncluded: values.pdfOptions?.isDrLetterIncluded ?? false,
          isPharmLetterIncluded:
            values.pdfOptions?.isPharmLetterIncluded ?? false,
          isLargeFontSize: values.pdfOptions?.isLargeFontSize ?? false,
          isSubheadingIncluded:
            values.pdfOptions?.isSubheadingIncluded ?? false,
          extraRows: values.pdfOptions?.extraRows ?? 0,
        },
      });
    }
  };

  const onDownload = async () => {
    let activeItemId = currentId.current;
    if (
      !activeItemId ||
      activeItem?.profileStatus === MedProfileStatusEnum.IN_PROGRESS
    ) {
      const result = await onFinaliseProfile(
        MedProfileStatusEnum.IN_PROGRESS,
        false,
        'download',
      );
      if (result) {
        activeItemId = currentId.current;
      }
    }

    if (activeItemId && access_token) {
      const values = medicationProfileForm.getValues();
      postGetPatientMedProfilePdf('download', {
        payload: {
          medProfileId: activeItemId,
          access_token: access_token,
        },
        pdfOptions: {
          viewMode: ViewMode.DOWNLOAD,
        },
        options: {
          isViewTime: values.pdfOptions?.isViewTime ?? true,
          isViewIcon: values.pdfOptions?.isViewIcon ?? false,
          isDrLetterIncluded: values.pdfOptions?.isDrLetterIncluded ?? false,
          isPharmLetterIncluded:
            values.pdfOptions?.isPharmLetterIncluded ?? false,
          isLargeFontSize: values.pdfOptions?.isLargeFontSize ?? false,
          isSubheadingIncluded:
            values.pdfOptions?.isSubheadingIncluded ?? false,
          extraRows: values.pdfOptions?.extraRows ?? 0,
        },
      });
    }
  };

  useEffect(() => {
    postGetAllCatalogOrderSets();
  }, []);

  return (
    <div className="medication-profile-body" style={{ position: 'relative' }}>
      {/* @ts-expect-error */}
      <MedicationProfileTitle
        actions={
          canEditMedicationProfile
            ? [
                {
                  name: 'Add order set',
                  icon: <PlusCircle />,
                  onClickAction: onAddOrderSet,
                  variant: 'btnSecondary',
                },
                {
                  name: 'Add medication',
                  icon: <PlusCircle />,
                  onClickAction: () => onOpenAddMedication(),
                },
              ]
            : []
        }
        title="Medication profile"
        showBackButton
        onBack={() => {
          if (isDirty) {
            setConfirmResetOpen(true);
            return;
          }
          onChangeTab(MedicationProfileSteps.FULL_LIST);
        }}
        titleClassName="medication-profile-title mb-0"
      />
      {/* Add medication */}
      {canEditMedicationProfile && (
        <AddMedicationForm
          open={openAddMedication}
          onClose={onCloseAddMedication}
          medicationProfileForm={medicationProfileForm}
          onSave={onAddMedicationToSection}
        />
      )}
      {/* Add order set */}
      {canEditMedicationProfile && (
        <AddOrderSet
          open={openAddOrderSet}
          onClose={onCloseAddOrderSet}
          orderSetData={orderSetData}
          onAddOrderSetToSection={onAddOrderSetToSection}
        />
      )}
      {/* Search Medication Catalogue */}
      <MedicationProfileCatalogueProvider
        enabled={Boolean(createMedProfile && shouldPrefillFromTimelineMeds)}
        onCloseCatalogueSection={() => setSelectingCatalogue(false)}
        medSections={medSections}
        setMedSections={setMedSectionsForCatalogue}
      >
        {selectingCatalogue ? (
          <CreateMedProfileCatalogueSection
            onClose={() => setSelectingCatalogue(false)}
          />
        ) : null}

        {/* Medicines Info */}
        <MedicationInfos
          // sectionsData={medSections}
          canEditMedicationProfile={!!canEditMedicationProfile}
          onAddSection={onAddSection}
          onEditSection={onEditSection}
          onRemoveSection={onRemoveSection}
          onRemoveMedicationFromSection={onRemoveMedicationFromSection}
          medicationProfileForm={medicationProfileForm}
          onEditMedicationToSection={onEditMedicationToSection}
          profileStatus={activeItem?.profileStatus}
        />
      </MedicationProfileCatalogueProvider>
      {/* Appointments */}
      <Appointments
        canEdit={canEditMedicationProfile}
        appointments={appointments}
        onAddAppointment={onAddAppointment}
        onDeleteAppointment={(index: number) => removeAppointment(index)}
        onEditAppointment={(values: Appointment, index: number) =>
          onEditAppointment(values, index)
        }
      />
      <ExportSettings
        createMedProfile={createMedProfile}
        form={medicationProfileForm}
        canEdit={!!canEditMedicationProfile}
        onPreview={onPreview}
        onDownload={onDownload}
        isPreviewLoading={
          isSubmitting === 'preview' || isLoadingPdf === 'preview'
        }
        isDownloadLoading={
          isSubmitting === 'download' || isLoadingPdf === 'download'
        }
      />
      <PharmacistDoctorLetterSection
        medProfile={
          { ...activeItem, ...medicationProfileForm.getValues() } as MedProfile
        }
        onProvideValues={registerLettersGetter}
      />
      <div className="d-flex flex-column gap-3">
        {canEditMedicationProfile && !showFinaliseSection && (
          <CareMapprButton
            onClick={() => onFinaliseProfile(MedProfileStatusEnum.IN_PROGRESS)}
            extraClassNames="action-button"
            label="Save as in progress"
            variant="secondary"
            style={{ width: '100%' }}
            disabled={isUpdating || isCreating || notableToEdit}
          />
        )}
        {showFinaliseSection ? (
          <Row>
            <Col md={8} lg={8} className="text-right mt-4">
              <span>
                <img src={actionCompletedIcon} alt="Finalised" /> Finalised
              </span>
            </Col>
            <Col md={4} lg={4}>
              <PatientAppInviteCard
                activeTitle={
                  patientMedProfileActive
                    ? 'Patient app active'
                    : 'Patient app not active'
                }
                description={
                  patientMedProfileActive || sent
                    ? 'The patient has been sent a text message with a link to access their profile.'
                    : 'Send the patient a link to access their med profile.'
                }
                buttonName="Send code to patient"
                numberFieldTitle="Confirm patient's phone number"
                sent={sent}
                onInvitePatient={async (phone) => {
                  const { success } = await inviteMedProfilePatient(phone);
                  if (!success) {
                    setToast('Invite failed. Please try again.', {
                      autohide: true,
                    });
                  }
                }}
                loading={isInvitingPatient}
                showButton={!patientMedProfileActive}
              />
            </Col>
          </Row>
        ) : (
          canEditMedicationProfile && (
            <FinaliseButton
              onFinalise={() =>
                onFinaliseProfile(MedProfileStatusEnum.FINALISED)
              }
              disabled={isUpdating || isCreating || notableToEdit}
            />
          )
        )}
      </div>
      <CaremapprConfirmation
        open={confirmResetOpen}
        text="This operation will reset unsaved values. Are you sure?"
        icon="warning"
        onConfirm={() => {
          onChangeTab(MedicationProfileSteps.FULL_LIST);
        }}
        onClose={() => setConfirmResetOpen(false)}
        showCancelButton
      />
    </div>
  );
};

export default MedicationProfileDetail;
